import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API });

// Attach the admin token (if the browser has one) to every request. The
// backend ignores this header on public GETs but requires it for any mutating
// endpoint. This means public visitors never accidentally trigger admin flows.
http.interceptors.request.use((config) => {
  const token = localStorage.getItem("bb_admin_token");
  if (token) config.headers["X-Admin-Token"] = token;
  return config;
});

export const adminAuth = {
  isLoggedIn: () => !!localStorage.getItem("bb_admin_token"),
  login: async (password) => {
    const { data } = await http.post("/admin/login", { password });
    localStorage.setItem("bb_admin_token", data.token);
    return true;
  },
  // Verify the token in localStorage against the backend. Returns true only
  // if the backend confirms the token is genuine — this prevents anyone from
  // opening DevTools and setting a fake value to bypass the UI gate.
  verify: async () => {
    const token = localStorage.getItem("bb_admin_token");
    if (!token) return false;
    try {
      await http.post("/admin/login", { password: token });
      return true;
    } catch {
      localStorage.removeItem("bb_admin_token");
      return false;
    }
  },
  logout: () => localStorage.removeItem("bb_admin_token"),
};

export const api = {
  listEntries: (params = {}) => http.get("/entries", { params }).then((r) => r.data),
  getEntry: (slug) => http.get(`/entries/${slug}`).then((r) => r.data),
  createEntry: (payload) => http.post("/entries", payload).then((r) => r.data),
  publishKitchenDesk: (payload) => http.post("/kitchen-desk/publish", payload).then((r) => r.data),
  autofillKitchenDesk: (title) =>
    http.post("/kitchen-desk/autofill", { title, generate_image: true }, { timeout: 180000 }).then((r) => r.data),
  updateEntry: (slug, payload) => http.put(`/entries/${slug}`, payload).then((r) => r.data),
  deleteEntry: (slug) => http.delete(`/entries/${slug}`).then((r) => r.data),
  generateEntry: (term) => http.post("/entries/generate", { term }).then((r) => r.data),
  generateRecipe: (term_slug) => http.post("/recipes/generate", { term_slug }).then((r) => r.data),

  listRecipes: (params = {}) => http.get("/recipes", { params }).then((r) => r.data),
  getRecipe: (slug) => http.get(`/recipes/${slug}`).then((r) => r.data),
  createRecipe: (payload) => http.post("/recipes", payload).then((r) => r.data),
  updateRecipe: (slug, payload) => http.put(`/recipes/${slug}`, payload).then((r) => r.data),
  deleteRecipe: (slug) => http.delete(`/recipes/${slug}`).then((r) => r.data),

  categories: () => http.get("/categories").then((r) => r.data),
  stats: () => http.get("/stats").then((r) => r.data),
  seed: (force = false) => http.post(`/seed?force=${force}`).then((r) => r.data),
  generateImage: (prompt) => http.post("/generate-image", { prompt }).then((r) => r.data),

  // --- Payments ($2.25 per recipe download via Stripe Checkout) ---
  createCheckoutSession: (slug) =>
    http.post("/payments/checkout", { slug, origin_url: window.location.origin }).then((r) => r.data),
  paymentStatus: (session_id) =>
    http.get(`/payments/status/${session_id}`).then((r) => r.data),
};

export default api;
